import { lazy, memo, Suspense, useEffect, useMemo, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import SideMenu from '../../Common/Menus/SideMenu.jsx';
import AddItemsMenu from '../AddItemComponents/AddItemsMenu.jsx';
import ClassSelectorMenu from './Menus/ClassSelectorMenu.jsx';
import SimpleElementSettings from './Menus/SimpleElementSettings.jsx';
import MobileElementActionBar from './Menus/MobileElementActionBar.jsx';
import { EMPTY_LAYER_MOVE_OPTIONS } from '../utilities/layerMoveUtils.js';
import {
  duplicateNodeWithNewIds,
  SAFE_TAGS,
  isTextNode,
  isValidClassName,
  isVoidElement,
  normalizeAttributes,
  normalizeClassList,
  reactAttributesForValidatedNode,
  sanitizeClassDefinitions,
  sanitizeNode,
  updateNodeById,
} from '../utilities/htmlEditorUtils.js';
import { getImage, getSharedImage } from '../../Common/Images/utilities/imageUtils.js';
import {
  getEditorActionViewport,
  getEditorItemClassName,
  getElementActionButtonClassName,
  SKILLS_ICON_CLASS,
  snapQuickActionsToViewport,
} from '../utilities/editorUiUtils.js';
import {
  getCollectionKind,
  getManagedNodeCapabilities,
  getCollectionRecordIds,
  isManagedEditorDecoration,
  updateCollectionSelection,
} from '../utilities/htmlCollectionUtils.js';
import useNestedElementDrag from '../DragAndDrop/useNestedElementDrag.js';
import {
  canPortfolioElementContainElements,
  getPortfolioEditElement,
} from '../utilities/portfolioElementRegistry.jsx';
import useEscapeToClose from '../utilities/useEscapeToClose.js';
import useAnimatedPresence from '../utilities/useAnimatedPresence.js';
import {
  applySimpleAppearancePreset,
  EDITOR_MODES,
  isSimpleSectionNode,
} from '../utilities/simpleEditorMode.js';
import emptyElementDisplay from '../config/emptyElementDisplay.json';

const ImageGallery = lazy(() => import('../../Common/Images/ImageGallery.jsx'));
const ProjectsGallery = lazy(() => import('../../ProjectsAndSkills/Projects/ProjectsGallery.jsx'));
const SkillsGallery = lazy(() => import('../../ProjectsAndSkills/Skills/SkillsGallery.jsx'));
const INTRINSIC_INLINE_TAGS = new Set([
  'a', 'code', 'em', 'i', 'mark', 'small', 'span', 'strong', 'time',
]);
const HIDDEN_EMPTY_STATE_TAGS = new Set(
  (emptyElementDisplay.hideEmptyStateForTags || [])
    .map((tagName) => String(tagName).trim().toLowerCase())
    .filter(Boolean)
);

const focusedControlFallback = (
  <div className="p-3 text-center" role="status">
    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
    <span className="visually-hidden">Loading editor controls</span>
  </div>
);

const COMMON_ATTRIBUTES = [
  'id', 'title', 'href', 'target', 'rel', 'src', 'alt', 'type', 'poster',
  'width', 'height', 'loading', 'controls', 'autoplay', 'loop', 'muted',
  'role', 'aria-label',
];

const BOOLEAN_ATTRIBUTES = new Set(['controls', 'autoplay', 'loop', 'muted']);

const isBootstrapColumnLayoutClass = (className) => (
  /^col(?:-(?:(?:sm|md|lg|xl|xxl)(?:-(?:auto|[1-9]|1[0-2]))?|auto|[1-9]|1[0-2]))?$/.test(className)
  || /^offset(?:-(?:sm|md|lg|xl|xxl))?-(?:[0-9]|1[0-2])$/.test(className)
  || /^order(?:-(?:sm|md|lg|xl|xxl))?-(?:first|last|[0-5])$/.test(className)
);

const displayName = (node) => isTextNode(node) ? 'text' : node?.type || 'element';

const findRecordNode = (node, kind, recordId) => {
  if (!node || isTextNode(node)) return null;
  if (String(node.attributes?.[`data-${kind}-id`] || '') === String(recordId)) return node;

  for (const child of node.children || []) {
    const match = findRecordNode(child, kind, recordId);
    if (match) return match;
  }
  return null;
};

const firstVisibleRecordText = (node) => {
  if (!node) return '';
  if (isTextNode(node)) return String(node.content || '').trim();

  for (const child of node.children || []) {
    const text = firstVisibleRecordText(child);
    if (text) return text;
  }
  return '';
};

const renderTextNodeContent = (node) => (
  <span className="html-text-node">{node?.content || 'Empty text'}</span>
);

const normalizeAttributeInputValue = (name, value) => (
  BOOLEAN_ATTRIBUTES.has(String(name || '').toLowerCase())
    ? Boolean(value === true || String(value || '').trim() === '' || String(value).toLowerCase() === 'true')
    : value
);

const NO_DRAG = Object.freeze({
  active: false,
  canDrag: false,
  isDragging: false,
  dropPosition: null,
  clearDragState: () => {},
});

const createControlState = (tagInput) => ({
  showAddMenu: false,
  newAttributeName: '',
  newAttributeValue: '',
  showClassSelector: false,
  showGallery: false,
  showProjectsGallery: false,
  showSkillsGallery: false,
  collectionBusy: false,
  localError: '',
  tagInput,
  insertionMode: 'child',
});
const CONTROL_STATE_KEYS = Object.keys(createControlState(''));

const controlStateReducer = (state, { key, value }) => ({
  ...state,
  [key]: typeof value === 'function' ? value(state[key]) : value,
});

const useFocusedControlState = (initialTag) => {
  const [state, dispatch] = useReducer(controlStateReducer, initialTag, createControlState);
  const setters = useMemo(() => CONTROL_STATE_KEYS.reduce((result, key) => {
    result[key] = (value) => dispatch({ key, value });
    return result;
  }, {}), []);
  return [state, setters];
};

const EditItemContent = ({
  index,
  siblingCount = 1,
  item,
  editJSONItem,
  addItem,
  swapItems,
  removeItem,
  focusedNodeId,
  editPanelNodeId,
  activeEditorPathIds,
  compactActions = false,
  onOpenPhoneRearrange,
  setEditPanelNodeId,
  setDrag,
  dragEnabled = true,
  dragSupported = dragEnabled,
  onRootDragStart,
  onRootDragEnd,
  onMoveNode,
  onUpdateNode,
  onInsertNode,
  onRemoveNode,
  onReorderNode,
  layerMoveOptions = EMPTY_LAYER_MOVE_OPTIONS,
  onMoveUpLayer,
  onOpenLayerMoveMenu,
  userId,
  editorDocument,
  setEditorDocument,
  disallowedItems = [],
  onOpenClassStyleMenu,
  aiEnabled = false,
  requestConfirmation,
  resolvedCustomClasses = null,
  depth = 0,
  insideBootstrapRow = false,
  collectionController = null,
  nestedDrag,
  controlStateBundle,
  editorMode = EDITOR_MODES.ADVANCED,
  onOpenAISection,
}) => {
  const [controlState, controlSetters] = controlStateBundle;
  const itemRef = useRef(null);
  const confirmAction = (options) => {
    if (typeof requestConfirmation === 'function') {
      requestConfirmation(options);
    } else {
      options.onConfirm?.();
    }
  };
  const {
    showAddMenu,
    newAttributeName,
    newAttributeValue,
    showClassSelector,
    showGallery,
    showProjectsGallery,
    showSkillsGallery,
    collectionBusy,
    localError,
    tagInput,
    insertionMode,
  } = controlState;
  const {
    showAddMenu: setShowAddMenu,
    newAttributeName: setNewAttributeName,
    newAttributeValue: setNewAttributeValue,
    showClassSelector: setShowClassSelector,
    showGallery: setShowGallery,
    showProjectsGallery: setShowProjectsGallery,
    showSkillsGallery: setShowSkillsGallery,
    collectionBusy: setCollectionBusy,
    localError: setLocalError,
    tagInput: setTagInput,
    insertionMode: setInsertionMode,
  } = controlSetters;

  const safeItem = item;
  const simpleMode = editorMode === EDITOR_MODES.SIMPLE;
  // The root sanitizes document classes once; recursive children reuse the same immutable view.
  const customClasses = useMemo(() => (
    resolvedCustomClasses || sanitizeClassDefinitions(editorDocument?.classes)
  ), [editorDocument?.classes, resolvedCustomClasses]);
  const nodeClasses = useMemo(
    () => normalizeClassList(safeItem?.attributes?.class),
    [safeItem?.attributes?.class]
  );
  const usesIntrinsicBootstrapButtonSize = nodeClasses.includes('btn');
  const usesIntrinsicInlineSize = INTRINSIC_INLINE_TAGS.has(safeItem?.type);
  const columnLayoutClasses = insideBootstrapRow
    ? nodeClasses.filter(isBootstrapColumnLayoutClass)
    : [];
  const firstAppliedCustomClass = nodeClasses.find((className) => customClasses[className]) || '';
  const textChild = safeItem?.children?.find(isTextNode);
  const isManagedDecoration = isManagedEditorDecoration(safeItem);
  const isFocused = !isManagedDecoration && focusedNodeId === safeItem?.id;
  const isEditing = !isManagedDecoration && editPanelNodeId === safeItem?.id;
  const renderEditMenu = useAnimatedPresence(isEditing);
  const renderAddMenu = useAnimatedPresence(showAddMenu);
  const renderClassSelector = useAnimatedPresence(showClassSelector);
  const collectionKind = getCollectionKind(safeItem);
  const collectionRecordIds = getCollectionRecordIds(safeItem);
  const managedRole = safeItem?.attributes?.['data-managed-role'];
  const canManageFromSubItem = ['project-item', 'skill-item', 'skill-item-content'].includes(managedRole);
  const activeCollectionKind = collectionKind || (canManageFromSubItem ? collectionController?.kind : null);
  const activeCollectionRecordIds = collectionKind
    ? collectionRecordIds
    : canManageFromSubItem ? collectionController?.recordIds || [] : [];
  const isManagedRatingIcon = safeItem?.attributes?.['data-managed-role'] === 'rating-star';
  const capabilities = getManagedNodeCapabilities(safeItem);
  const canHaveChildren = Boolean(safeItem && canPortfolioElementContainElements(safeItem.type));
  const isEmptyContainer = canHaveChildren
    && capabilities.canAddChild
    && (safeItem.children || []).length === 0;
  const hasDragHandler = depth === 0 ? typeof onRootDragStart === 'function' : nestedDrag.canDrag;
  const canDragElement = dragEnabled && capabilities.canMove && hasDragHandler;
  const startElementDrag = depth === 0 ? onRootDragStart : nestedDrag.handleDragStart;
  const endElementDrag = depth === 0 ? onRootDragEnd : nestedDrag.clearDragState;

  useEffect(() => {
    if (safeItem?.type) setTagInput(safeItem.type);
  }, [safeItem?.type, setTagInput]);

  useEffect(() => {
    if (!isFocused) setShowClassSelector(false);
  }, [isFocused, setShowClassSelector]);

  useEffect(() => {
    if (!isFocused || compactActions) return undefined;

    let frameId = null;
    let requestedScroll = false;
    const keepActionsVisible = () => {
      const itemElement = itemRef.current;
      const actionLayer = itemElement?.querySelector('.quick-element-actions-layer');
      const hasClippedAction = snapQuickActionsToViewport(
        actionLayer,
        getEditorActionViewport(document)
      );

      if (hasClippedAction && !requestedScroll) {
        requestedScroll = true;
        itemElement?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    };
    const scheduleVisibilityCheck = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(keepActionsVisible);
    };

    scheduleVisibilityCheck();
    window.addEventListener('resize', scheduleVisibilityCheck);
    window.addEventListener('scroll', scheduleVisibilityCheck, true);
    window.visualViewport?.addEventListener('resize', scheduleVisibilityCheck);
    window.visualViewport?.addEventListener('scroll', scheduleVisibilityCheck);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(scheduleVisibilityCheck)
      : null;
    if (itemRef.current) {
      resizeObserver?.observe(itemRef.current);
      itemRef.current.querySelectorAll([
        '.quick-element-actions-layer',
        '.quick-element-actions-top-center',
        '.quick-element-actions-drag',
        '.quick-element-actions-child',
        '.quick-element-actions-bottom-center',
      ].join(',')).forEach((element) => resizeObserver?.observe(element));
    }
    document.querySelectorAll('.selection-navigator, .new-blog-button-container')
      .forEach((element) => resizeObserver?.observe(element));

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleVisibilityCheck);
      window.removeEventListener('scroll', scheduleVisibilityCheck, true);
      window.visualViewport?.removeEventListener('resize', scheduleVisibilityCheck);
      window.visualViewport?.removeEventListener('scroll', scheduleVisibilityCheck);
    };
  }, [activeCollectionKind, aiEnabled, canDragElement, compactActions, editorMode, isFocused, safeItem?.id]);

  useEscapeToClose(
    isEditing || showClassSelector || showGallery || showProjectsGallery || showSkillsGallery,
    () => {
      if (showGallery) setShowGallery(false);
      else if (showProjectsGallery) setShowProjectsGallery(false);
      else if (showSkillsGallery) setShowSkillsGallery(false);
      else if (showClassSelector) setShowClassSelector(false);
      else if (isEditing) setEditPanelNodeId?.(null);
      setDrag?.(true);
    }
  );

  if (!safeItem) {
    return null;
  }

  const updateItem = (updater) => {
    if (onUpdateNode) {
      onUpdateNode(safeItem.id, updater);
      return;
    }
    const updated = typeof updater === 'function' ? updater(safeItem) : updater;
    editJSONItem(index, sanitizeNode(updated));
  };

  const setAttribute = (name, value) => {
    updateItem((node) => ({
      ...node,
      attributes: normalizeAttributes({
        ...(node.attributes || {}),
        [name]: value,
      }, node.type),
    }));
  };

  const removeAttribute = (name) => {
    updateItem((node) => {
      const attributes = { ...(node.attributes || {}) };
      delete attributes[name];
      return {
        ...node,
        attributes: normalizeAttributes(attributes, node.type),
      };
    });
  };

  const addAttribute = () => {
    const attributeName = newAttributeName.trim();
    const normalizedAttributeName = attributeName.toLowerCase();

    if (!attributeName) {
      return;
    }

    if (normalizedAttributeName === 'class' || normalizedAttributeName === 'classname') {
      setLocalError('Use the Classes section to change appearance classes.');
      return;
    }

    const existingAttributeName = Object.keys(safeItem.attributes || {})
      .find((name) => name.toLowerCase() === normalizedAttributeName);
    if (existingAttributeName) {
      setLocalError(`The "${attributeName}" setting has already been added.`);
      return;
    }

    setLocalError('');
    setAttribute(normalizedAttributeName, normalizeAttributeInputValue(normalizedAttributeName, newAttributeValue));
    setNewAttributeName('');
    setNewAttributeValue('');
  };

  const updateTextContent = (content) => {
    updateItem((node) => {
      if (isTextNode(node)) {
        return { ...node, content };
      }

      const children = Array.isArray(node.children) ? [...node.children] : [];
      const textIndex = children.findIndex(isTextNode);
      if (textIndex >= 0) {
        children[textIndex] = { ...children[textIndex], content };
      } else {
        children.unshift({ id: `text-${Date.now()}`, type: 'text', content });
      }

      return { ...node, children };
    });
  };

  const addClass = (className) => {
    const safeClass = String(className || '').trim();
    if (!isValidClassName(safeClass)) {
      setLocalError('Class names must start with a letter, underscore, or hyphen and contain only letters, numbers, underscores, or hyphens.');
      return;
    }

    setLocalError('');
    updateItem((node) => ({
      ...node,
      attributes: {
        ...(node.attributes || {}),
        class: normalizeClassList([...(node.attributes?.class || []), safeClass]),
      },
    }));
  };

  const applySimpleAppearance = (group, value) => {
    updateItem((node) => applySimpleAppearancePreset(node, group, value));
  };

  const removeClass = (className) => {
    updateItem((node) => ({
      ...node,
      attributes: {
        ...(node.attributes || {}),
        class: normalizeClassList(node.attributes?.class).filter((entry) => entry !== className),
      },
    }));
  };

  const createCustomClass = (className) => {
    const safeClass = String(className || '').trim();
    setEditorDocument((previous) => ({
      ...previous,
      root: updateNodeById(previous.root, safeItem.id, (node) => ({
        ...node,
        attributes: {
          ...(node.attributes || {}),
          class: normalizeClassList([...(node.attributes?.class || []), safeClass]),
        },
      })),
      classes: {
        ...(previous.classes || {}),
        [safeClass]: previous.classes?.[safeClass] || { base: {}, sm: {}, md: {}, lg: {}, xl: {} },
      },
    }));
  };

  const toggleClass = (className) => {
    if (nodeClasses.includes(className)) {
      removeClass(className);
    } else {
      addClass(className);
    }
  };

  const openClassSelector = () => {
    setEditPanelNodeId?.(null);
    setShowClassSelector(true);
    setDrag?.(false);
  };

  const closeClassSelector = () => {
    setShowClassSelector(false);
    setDrag?.(true);
  };

  const openClassStylesFromSelector = (className) => {
    setShowClassSelector(false);
    onOpenClassStyleMenu?.(className);
  };

  const addChild = (childNode) => {
    if (onInsertNode) {
      onInsertNode(
        safeItem.id,
        childNode,
        isTextNode(safeItem) || isVoidElement(safeItem.type) ? 'after' : 'child'
      );
      return;
    }

    if (isTextNode(safeItem) || isVoidElement(safeItem.type)) {
      addItem(childNode, index + 1);
      return;
    }

    updateItem((node) => ({
      ...node,
      children: [...(node.children || []), childNode],
    }));
  };

  const editChild = onUpdateNode ? editJSONItem : (childIndex, childNode) => {
    updateItem((node) => {
      const children = [...(node.children || [])];
      children[childIndex] = childNode;
      return { ...node, children };
    });
  };

  const removeChild = onRemoveNode ? removeItem : (childIndex) => {
    confirmAction({
      title: 'Delete this item?',
      message: 'This item and everything inside it will be removed.',
      confirmLabel: 'Delete item',
      variant: 'danger',
      onConfirm: () => updateItem((node) => {
        const children = [...(node.children || [])];
        children.splice(childIndex, 1);
        return { ...node, children };
      }),
    });
  };

  const moveChild = onReorderNode ? swapItems : (childIndex, nextIndex) => {
    updateItem((node) => {
      const children = [...(node.children || [])];
      if (childIndex < 0 || childIndex >= children.length) {
        return node;
      }

      const boundedNextIndex = Math.max(0, Math.min(nextIndex, children.length - 1));
      const [moved] = children.splice(childIndex, 1);
      children.splice(boundedNextIndex, 0, moved);
      return { ...node, children };
    });
  };

  const duplicateSelf = () => {
    const duplicate = duplicateNodeWithNewIds(safeItem);
    if (onInsertNode) {
      onInsertNode(safeItem.id, duplicate, 'after');
    } else {
      addItem(duplicate, index + 1);
    }
  };

  const openAddMenu = (mode) => {
    setInsertionMode(mode);
    setShowAddMenu(true);
    setDrag?.(false);
  };

  const addForInsertionMode = (node) => {
    if (insertionMode === 'after') {
      if (onInsertNode) {
        onInsertNode(safeItem.id, node, 'after');
      } else {
        addItem(node, index + 1);
      }
      return;
    }

    addChild(node);
  };

  const removeSelf = () => {
    confirmAction({
      title: 'Delete this item?',
      message: 'This item and everything inside it will be removed from the page.',
      confirmLabel: 'Delete item',
      variant: 'danger',
      onConfirm: () => {
        if (onRemoveNode) onRemoveNode(safeItem.id);
        else removeItem(index);
      },
    });
  };

  const reorderSelf = (offset) => {
    if (onReorderNode) {
      onReorderNode(safeItem.id, offset);
    } else {
      swapItems(index, index + offset);
    }
  };

  const commitTagType = () => {
    const nextType = String(tagInput || '').trim().toLowerCase();
    if (!nextType || !SAFE_TAGS.has(nextType)) {
      setLocalError(nextType ? `The "${nextType}" item type is not available.` : 'Enter an item type.');
      setTagInput(safeItem.type);
      return;
    }

    setLocalError('');
    setTagInput(nextType);
    if (nextType === safeItem.type) return;

    updateItem((node) => ({
      ...node,
      type: nextType,
      attributes: normalizeAttributes(node.attributes, nextType),
      children: isVoidElement(nextType) ? [] : node.children,
    }));
  };

  const openEditPanel = () => {
    setEditPanelNodeId?.(safeItem.id);
    setDrag?.(false);
  };

  const updateOwnCollectionRecords = async (recordIds) => {
    if (!collectionKind) return;
    const updatedCollection = await updateCollectionSelection(safeItem, recordIds, userId);
    updateItem(updatedCollection);
  };

  const applyCollectionIds = async (recordIds) => {
    if (!activeCollectionKind) return;
    setCollectionBusy(true);
    setLocalError('');
    try {
      if (collectionKind) {
        await updateOwnCollectionRecords(recordIds);
      } else {
        await collectionController?.onUpdateRecords(recordIds);
      }
    } catch (error) {
      setLocalError(error.message || `Failed to update ${activeCollectionKind} collection.`);
    } finally {
      setCollectionBusy(false);
      setShowProjectsGallery(false);
      setShowSkillsGallery(false);
    }
  };

  const openCollectionGallery = () => {
    if (activeCollectionKind === 'project') setShowProjectsGallery(true);
    if (activeCollectionKind === 'skill') setShowSkillsGallery(true);
  };

  const moveCollectionRecord = (recordIndex, offset) => {
    const targetIndex = recordIndex + offset;
    if (targetIndex < 0 || targetIndex >= activeCollectionRecordIds.length) return;
    const nextIds = [...activeCollectionRecordIds];
    const [movedId] = nextIds.splice(recordIndex, 1);
    nextIds.splice(targetIndex, 0, movedId);
    void applyCollectionIds(nextIds);
  };

  const removeCollectionRecord = (recordId) => {
    confirmAction({
      title: `Remove this ${activeCollectionKind}?`,
      message: `This ${activeCollectionKind} will no longer appear in this section. Your saved ${activeCollectionKind} will not be deleted.`,
      confirmLabel: `Remove ${activeCollectionKind}`,
      variant: 'danger',
      onConfirm: () => applyCollectionIds(activeCollectionRecordIds.filter((id) => id !== recordId)),
    });
  };

  const descendantCollectionController = collectionKind ? {
    kind: collectionKind,
    recordIds: collectionRecordIds,
    onUpdateRecords: updateOwnCollectionRecords,
  } : collectionController;

  const elementActions = [
    {
      key: 'edit',
      group: 'edit',
      iconClass: 'bi-pencil-square',
      title: 'Edit',
      onClick: openEditPanel,
      visible: !isManagedDecoration,
      quickOnly: true,
    },
    {
      key: 'move-up',
      group: 'movement',
      iconClass: 'bi-arrow-bar-up',
      title: 'Move up',
      onClick: () => reorderSelf(-1),
      visible: capabilities.canMove,
      disabled: index <= 0,
    },
    {
      key: 'move-down',
      group: 'movement',
      iconClass: 'bi-arrow-bar-down',
      title: 'Move down',
      onClick: () => reorderSelf(1),
      visible: capabilities.canMove,
      disabled: index >= siblingCount - 1,
    },
    {
      key: 'move-up-layer',
      group: 'hierarchy',
      iconClass: 'bi-box-arrow-up',
      title: 'Move out of parent',
      onClick: () => onMoveUpLayer?.(safeItem.id),
      visible: capabilities.canChangeLayer,
      advancedOnly: true,
      disabled: !layerMoveOptions.canMoveUp,
      quickOnly: true,
    },
    {
      key: 'move-down-layer',
      group: 'hierarchy',
      iconClass: 'bi-box-arrow-in-down',
      title: 'Move into container',
      onClick: () => {
        setShowAddMenu(false);
        setShowClassSelector(false);
        onOpenLayerMoveMenu?.(safeItem.id);
      },
      visible: capabilities.canChangeLayer,
      advancedOnly: true,
      disabled: layerMoveOptions.parentCandidates.length === 0,
      quickOnly: true,
    },
    {
      key: 'add-record',
      group: 'side',
      iconClass: activeCollectionKind === 'skill' ? SKILLS_ICON_CLASS : 'bi-kanban',
      title: `Add ${activeCollectionKind || ''}`,
      onClick: openCollectionGallery,
      visible: Boolean(activeCollectionKind),
    },
    {
      key: 'add-child',
      group: 'side',
      iconClass: 'bi-file-earmark-plus',
      title: simpleMode ? 'Add content inside' : 'Add child',
      onClick: () => openAddMenu('child'),
      visible: capabilities.canAddChild && !isEmptyContainer && (!simpleMode || isSimpleSectionNode(safeItem)),
    },
    {
      key: 'duplicate',
      group: 'secondary',
      iconClass: 'bi-files',
      title: 'Duplicate',
      onClick: duplicateSelf,
      visible: capabilities.canDuplicate,
    },
    {
      key: 'delete',
      group: 'delete',
      iconClass: 'bi-trash',
      title: 'Delete',
      danger: true,
      onClick: removeSelf,
      visible: capabilities.canDelete,
    },
  ];

  const renderElementActionButtons = ({ quick = false, group = null } = {}) => (
    elementActions
      .filter((action) => action.visible !== false)
      .filter((action) => !simpleMode || !action.advancedOnly)
      .filter((action) => quick || !action.quickOnly)
      .filter((action) => !group || action.group === group)
      .map(({ key, iconClass, title, danger = false, disabled = false, onClick }) => (
        <button
          key={key}
          className={getElementActionButtonClassName({ danger })}
          type="button"
          disabled={disabled}
          aria-label={title}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          title={title}
        >
          <i className={`bi ${iconClass}`}></i>
        </button>
      ))
  );

  const hasQuickActions = (group) => elementActions.some((action) => (
    action.group === group && action.visible !== false && (!simpleMode || !action.advancedOnly)
  ));
  const visibleQuickActions = elementActions.filter((action) => (
    action.visible !== false && (!simpleMode || !action.advancedOnly)
  ));
  const mobileEditAction = visibleQuickActions.find((action) => action.key === 'edit');
  const mobileAiAction = visibleQuickActions.find((action) => action.key === 'ai-edit');
  const mobileMoveActions = visibleQuickActions.filter((action) => (
    action.group === 'movement' || action.group === 'hierarchy'
  ));
  const mobileMoreActions = [
    ...visibleQuickActions.filter((action) => (
      ['side', 'secondary'].includes(action.group) && action.key !== 'ai-edit'
    )),
    ...(capabilities.canInsertSibling ? [{
      key: 'add-below',
      iconClass: 'bi-plus-lg',
      title: simpleMode ? 'Add content below' : 'Add element below',
      onClick: () => openAddMenu('after'),
    }] : []),
    ...visibleQuickActions.filter((action) => action.group === 'delete'),
  ];

  const changeSelectedImage = async (imageJSON) => {
    setLocalError('');
    try {
      const imageData = userId
        ? await getImage(userId, imageJSON.imageId)
        : await getSharedImage(imageJSON.imageId);

      setAttribute('src', imageData?.base64 || '');
      setAttribute('alt', imageData?.alt || imageData?.filename || 'Image');
      setAttribute('data-image-id', imageJSON.imageId);
    } catch (error) {
      setLocalError(error.message || 'Failed to load selected image.');
    } finally {
      setShowGallery(false);
    }
  };

  const currentText = isTextNode(safeItem) ? safeItem.content : textChild?.content || '';
  const attributeEntries = Object.entries(safeItem.attributes || {}).filter(([name]) => name !== 'class');
  const editorItemClassName = [
    getEditorItemClassName({ isFocused, isEditing, depth }),
    usesIntrinsicBootstrapButtonSize ? 'html-edit-intrinsic-bootstrap-button' : '',
    usesIntrinsicInlineSize ? 'html-edit-intrinsic-inline' : '',
    nodeClasses.includes('skill-item') ? 'html-edit-skill-item' : '',
    columnLayoutClasses.length ? 'html-edit-bootstrap-column' : '',
    ...columnLayoutClasses,
    nestedDrag.isDragging ? 'nested-dragging' : '',
    nestedDrag.dropPosition ? `nested-drop-${nestedDrag.dropPosition}` : '',
  ].filter(Boolean).join(' ');

  const renderEditablePreview = () => {
    if (isTextNode(safeItem)) {
      return renderTextNodeContent(safeItem);
    }

    const EditElement = getPortfolioEditElement(safeItem.type);
    const previewProps = { ...reactAttributesForValidatedNode(safeItem) };
    const previewClasses = insideBootstrapRow
      ? nodeClasses.filter((className) => !isBootstrapColumnLayoutClass(className))
      : nodeClasses;
    previewProps.className = [...previewClasses, 'html-edit-node-content'].join(' ');

    if (isVoidElement(safeItem.type)) {
      return <EditElement {...previewProps} />;
    }

    return (
      <EditElement {...previewProps}>
        {safeItem.children?.length ? (
          safeItem.children.map((child, childIndex) => {
            if (isTextNode(child)) {
              return <span key={child.id || childIndex}>{child.content}</span>;
            }

            return (
              <EditItem
                key={child.id || childIndex}
                index={childIndex}
                siblingCount={safeItem.children.length}
                item={child}
                editJSONItem={editChild}
                addItem={onInsertNode ? addItem : (childNode, insertAtIndex = null) => {
                  updateItem((node) => {
                    const children = [...(node.children || [])];
                    const insertIndex = Number.isInteger(insertAtIndex) ? insertAtIndex : childIndex + 1;
                    children.splice(insertIndex, 0, childNode);
                    return { ...node, children };
                  });
                }}
                swapItems={moveChild}
                removeItem={removeChild}
                focusedNodeId={focusedNodeId}
                editPanelNodeId={editPanelNodeId}
                activeEditorPathIds={activeEditorPathIds}
                compactActions={compactActions}
                onOpenPhoneRearrange={onOpenPhoneRearrange}
                setEditPanelNodeId={setEditPanelNodeId}
                setDrag={setDrag}
                dragSupported={dragSupported}
                dragEnabled={dragEnabled}
                onMoveNode={onMoveNode}
                onUpdateNode={onUpdateNode}
                onInsertNode={onInsertNode}
                onRemoveNode={onRemoveNode}
                onReorderNode={onReorderNode}
                layerMoveOptions={layerMoveOptions}
                onMoveUpLayer={onMoveUpLayer}
                onOpenLayerMoveMenu={onOpenLayerMoveMenu}
                userId={userId}
                editorDocument={editorDocument}
                setEditorDocument={setEditorDocument}
                disallowedItems={disallowedItems}
                onOpenClassStyleMenu={onOpenClassStyleMenu}
                aiEnabled={aiEnabled}
                requestConfirmation={requestConfirmation}
                resolvedCustomClasses={customClasses}
                depth={depth + 1}
                insideBootstrapRow={nodeClasses.includes('row')}
                collectionController={descendantCollectionController}
                editorMode={editorMode}
                onOpenAISection={onOpenAISection}
              />
            );
          })
        ) : isEmptyContainer ? (
          <div
            className={`html-empty-container-drop-target ${nestedDrag.dropPosition === 'child' ? 'is-drop-active' : ''}`}
            data-empty-container-drop-target={safeItem.id}
            {...(nestedDrag.active ? {
              onDragOver: nestedDrag.handleChildDragOver,
              onDrop: nestedDrag.handleChildDrop,
              onDragLeave: nestedDrag.handleDragLeave,
            } : {})}
            aria-label={`Empty ${safeItem.type} drop target`}
          >
            <button
              className="btn btn-sm btn-outline-primary html-empty-container-add"
              type="button"
              onClick={() => openAddMenu('child')}
              aria-label={`Add child to empty ${safeItem.type}`}
              title={`Add child to empty ${safeItem.type}`}
            >
              <i className="bi bi-plus-lg" aria-hidden="true"></i>
              <span>Add child</span>
            </button>
          </div>
        ) : isManagedRatingIcon || HIDDEN_EMPTY_STATE_TAGS.has(safeItem.type) ? null : (
          <span className="html-empty-node">Empty {safeItem.type}</span>
        )}
      </EditElement>
    );
  };

  return (
    <div
      ref={itemRef}
      className={editorItemClassName}
      data-editor-depth={depth}
      data-editor-node-id={isManagedDecoration ? undefined : safeItem.id}
      data-editor-node-type={safeItem.type}
      {...(nestedDrag.active ? {
        onDragStartCapture: (event) => {
          const startedFromHandle = event.target instanceof Element
            && event.target.closest('[data-editor-drag-handle="true"]');
          if (!startedFromHandle) event.preventDefault();
        },
        onDragOver: nestedDrag.handleDragOver,
        onDrop: nestedDrag.handleDrop,
        onDragEnd: nestedDrag.clearDragState,
        onDragLeave: nestedDrag.handleDragLeave,
      } : {})}
    >
      {isFocused && !isTextNode(safeItem) ? (
        <div className="html-edit-item-label" aria-hidden="true">
          <span className="html-edit-item-label-tag">{safeItem.type}</span>
          {!simpleMode && nodeClasses.length > 0 ? (
            <span className="html-edit-item-label-classes">
              {nodeClasses.map((className) => `.${className}`).join(' ')}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="html-edit-preview">
        {renderEditablePreview()}
      </div>

      {isFocused
        && compactActions
        && !isEditing
        && !showAddMenu
        && !showClassSelector
        && !showGallery
        && !showProjectsGallery
        && !showSkillsGallery ? (
        <MobileElementActionBar
          elementType={safeItem.type}
          editAction={mobileEditAction}
          aiAction={mobileAiAction}
          moveActions={mobileMoveActions}
          moreActions={mobileMoreActions}
          onOpenRearrange={onOpenPhoneRearrange
            ? () => onOpenPhoneRearrange(safeItem.id)
            : null}
        />
      ) : null}

      {isFocused && !compactActions ? (
        <div
          className="quick-element-actions-layer"
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          {(hasQuickActions('movement') || hasQuickActions('hierarchy')) ? (
            <div className="quick-element-actions-top-center">
              {hasQuickActions('movement') ? (
                <div className="quick-element-actions quick-element-actions-movement" data-action-group="movement">
                  {renderElementActionButtons({ quick: true, group: 'movement' })}
                </div>
              ) : null}
              {hasQuickActions('hierarchy') ? (
                <div className="quick-element-actions quick-element-actions-hierarchy" data-action-group="hierarchy">
                  {renderElementActionButtons({ quick: true, group: 'hierarchy' })}
                </div>
              ) : null}
            </div>
          ) : null}
          {canDragElement ? (
            <div className="quick-element-actions quick-element-actions-drag" data-action-group="drag">
              <button
                className={getElementActionButtonClassName({})}
                type="button"
                data-editor-drag-handle="true"
                draggable
                onDragStart={startElementDrag}
                onDragEnd={endElementDrag}
                onClick={(event) => event.stopPropagation()}
                title="Drag element"
                aria-label="Drag element"
              >
                <i className="bi bi-grip-vertical"></i>
              </button>
            </div>
          ) : null}

          {hasQuickActions('side') ? (
            <div className="quick-element-actions quick-element-actions-child" data-action-group="child">
              {renderElementActionButtons({ quick: true, group: 'side' })}
            </div>
          ) : null}

          <div className="quick-element-actions-bottom-center">
            <div className="quick-element-actions quick-element-actions-edit" data-action-group="edit">
              {renderElementActionButtons({ quick: true, group: 'edit' })}
            </div>
            {(hasQuickActions('secondary') || capabilities.canInsertSibling) ? (
              <div className="quick-element-actions quick-element-actions-secondary" data-action-group="secondary">
                {renderElementActionButtons({ quick: true, group: 'secondary' })}
                {capabilities.canInsertSibling ? (
                  <button
                    className={getElementActionButtonClassName({})}
                    type="button"
                    onClick={() => openAddMenu('after')}
                    title={simpleMode ? 'Add content below' : 'Add element below'}
                    aria-label={simpleMode ? 'Add content below' : 'Add element below'}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true"></i>
                  </button>
                ) : null}
              </div>
            ) : null}
            {hasQuickActions('delete') ? (
              <div className="quick-element-actions quick-element-actions-delete" data-action-group="delete">
                {renderElementActionButtons({ quick: true, group: 'delete' })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Recursive documents can be large, so node-specific menus only mount while active. */}
      {renderEditMenu ? (
        <SideMenu
          className="new-blog-side-menu"
          animate
          side="right"
          title={simpleMode ? (isSimpleSectionNode(safeItem) ? 'Edit section' : 'Edit content') : `${displayName(safeItem)} settings`}
          iconClass="bi-sliders"
          expanded={isEditing}
          setExpanded={(value) => {
            setEditPanelNodeId?.(value ? safeItem.id : null);
            setDrag?.(!value);
          }}
        >
        {localError ? <div className="alert alert-danger small" role="alert">{localError}</div> : null}
        {safeItem.attributes?.['data-record-unavailable'] ? (
          <div className="alert alert-warning small">
            This linked record is unavailable. Its last saved content has been kept.
          </div>
        ) : null}

        <div className="element-action-grid mb-3">
          {renderElementActionButtons()}
        </div>

        {simpleMode ? (
          <SimpleElementSettings
            node={safeItem}
            currentText={currentText}
            onTextChange={updateTextContent}
            onSetAttribute={setAttribute}
            onRemoveAttribute={removeAttribute}
            onChangeImage={() => setShowGallery(true)}
            onApplyAppearance={applySimpleAppearance}
          />
        ) : null}

        {!simpleMode && !isTextNode(safeItem) && capabilities.canEditSource ? (
          <>
            <label className="form-label small fw-semibold" htmlFor={`html-editor-tag-${safeItem.id}`}>Item type</label>
            <input
              id={`html-editor-tag-${safeItem.id}`}
              className="form-control form-control-sm mb-3"
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              onBlur={commitTagType}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitTagType();
                }
                if (event.key === 'Escape') {
                  setTagInput(safeItem.type);
                }
              }}
              list="html-editor-tag-suggestions"
            />
            <datalist id="html-editor-tag-suggestions">
              {[...SAFE_TAGS].sort().map((tagName) => <option value={tagName} key={tagName} />)}
            </datalist>
          </>
        ) : null}

        {!simpleMode && capabilities.canEditSource && (isTextNode(safeItem) || !isVoidElement(safeItem.type)) ? (
          <>
            <label className="form-label small fw-semibold" htmlFor={`html-editor-text-${safeItem.id}`}>Text content</label>
            <textarea
              id={`html-editor-text-${safeItem.id}`}
              className="form-control element-text-content-input mb-3"
              rows="5"
              value={currentText}
              onChange={(event) => updateTextContent(event.target.value)}
            />
          </>
        ) : null}

        {!simpleMode && !isTextNode(safeItem) && capabilities.canEditSource ? (
          <>
            <h6 className="mt-3">Extra settings</h6>
            {attributeEntries.length > 0 ? (
              <div className="mb-3">
                {attributeEntries.map(([name, value]) => (
                  <div className="input-group input-group-sm mb-2" key={name}>
                    <span className="input-group-text">{name}</span>
                    {BOOLEAN_ATTRIBUTES.has(name.toLowerCase()) ? (
                      <div className="input-group-text bg-white">
                        <input
                          className="form-check-input mt-0"
                          type="checkbox"
                          checked={Boolean(value)}
                          onChange={(event) => setAttribute(name, event.target.checked)}
                          aria-label={`Toggle ${name}`}
                        />
                      </div>
                    ) : (
                      <input
                        className="form-control"
                        value={String(value)}
                        onChange={(event) => setAttribute(name, event.target.value)}
                        aria-label={`${name} value`}
                      />
                    )}
                    <button
                      className="btn btn-outline-danger"
                      type="button"
                      onClick={() => removeAttribute(name)}
                      title={`Remove ${name}`}
                      aria-label={`Remove ${name}`}
                    >
                      <i className="bi bi-x" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted small mb-3">No extra settings added yet.</p>
            )}

            <div className="input-group input-group-sm mb-3">
              <input
                className="form-control"
                placeholder="setting"
                aria-label="Setting name"
                list="html-editor-attribute-suggestions"
                value={newAttributeName}
                onChange={(event) => setNewAttributeName(event.target.value)}
              />
              <input
                className="form-control"
                placeholder="value"
                aria-label="Setting value"
                value={newAttributeValue}
                onChange={(event) => setNewAttributeValue(event.target.value)}
              />
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={addAttribute}
                title="Add setting"
                aria-label="Add setting"
              >
                <i className="bi bi-plus" aria-hidden="true"></i>
              </button>
            </div>
            <datalist id="html-editor-attribute-suggestions">
              {COMMON_ATTRIBUTES
                .filter((name) => name !== 'class' && !Object.prototype.hasOwnProperty.call(safeItem.attributes || {}, name))
                .map((name) => (
                  <option value={name} key={name} />
                ))}
            </datalist>

            {safeItem.type === 'img' ? (
              <button className="btn btn-primary btn-sm w-100 mb-3" type="button" onClick={() => setShowGallery(true)}>
                <i className="bi bi-image me-2" aria-hidden="true"></i>Change image
              </button>
            ) : null}

            {safeItem.type === 'a' ? (
              <button className="btn btn-outline-secondary btn-sm mb-3" type="button" onClick={() => {
                setAttribute('target', '_blank');
                setAttribute('rel', 'noopener noreferrer');
              }}>
                Open safely in new tab
              </button>
            ) : null}
          </>
        ) : null}

        {!simpleMode && !isTextNode(safeItem) ? (
          <>
            <h6 className="mt-3">Classes</h6>
            {nodeClasses.length > 0 ? (
              <div className="d-flex flex-wrap gap-1 mb-2" aria-label="Applied classes">
                {nodeClasses.map((className) => (
                  <span className={`badge ${customClasses[className] ? 'text-bg-primary' : 'text-bg-secondary'}`} key={className}>
                    {className}
                  </span>
                ))}
              </div>
            ) : <p className="text-muted small mb-2">No classes applied.</p>}

            <button className="btn btn-outline-primary btn-sm w-100 mb-2" type="button" onClick={openClassSelector}>
              <i className="bi bi-ui-checks-grid me-2" aria-hidden="true"></i>Choose classes
            </button>

            {Object.keys(customClasses).length > 0 ? (
              <button
                className="btn btn-outline-primary btn-sm w-100 mb-3"
                type="button"
                onClick={() => onOpenClassStyleMenu?.(firstAppliedCustomClass)}
              >
                <i className="bi bi-brush me-2" aria-hidden="true"></i>Open class editor
              </button>
            ) : null}
          </>
        ) : null}

        {activeCollectionKind ? (
          <div className="mb-3">
            <h6 className="mt-3">Included {activeCollectionKind === 'project' ? 'projects' : 'skills'}</h6>
            <p className="text-muted small" role="status" aria-live="polite">
              {activeCollectionRecordIds.length} selected
            </p>
            {activeCollectionRecordIds.length ? (
              <div className="list-group mb-2">
                {activeCollectionRecordIds.map((recordId, recordIndex) => (
                  <div className="list-group-item d-flex align-items-center gap-1 py-1 px-2" key={recordId}>
                    <span className="small text-truncate flex-grow-1">
                      {firstVisibleRecordText(findRecordNode(editorDocument?.root, activeCollectionKind, recordId))
                        || `${activeCollectionKind === 'project' ? 'Project' : 'Skill'} ${recordIndex + 1}`}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      type="button"
                      onClick={() => moveCollectionRecord(recordIndex, -1)}
                      disabled={collectionBusy || recordIndex === 0}
                      title="Move up"
                      aria-label={`Move ${activeCollectionKind} ${recordIndex + 1} up`}
                    >
                      <i className="bi bi-arrow-up" aria-hidden="true"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      type="button"
                      onClick={() => moveCollectionRecord(recordIndex, 1)}
                      disabled={collectionBusy || recordIndex === activeCollectionRecordIds.length - 1}
                      title="Move down"
                      aria-label={`Move ${activeCollectionKind} ${recordIndex + 1} down`}
                    >
                      <i className="bi bi-arrow-down" aria-hidden="true"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      type="button"
                      onClick={() => removeCollectionRecord(recordId)}
                      disabled={collectionBusy}
                      title="Remove"
                      aria-label={`Remove ${activeCollectionKind} ${recordIndex + 1}`}
                    >
                      <i className="bi bi-x-lg" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <button
              className="btn btn-outline-primary btn-sm w-100"
              type="button"
              onClick={openCollectionGallery}
              disabled={collectionBusy}
            >
              <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>
              {collectionBusy ? 'Loading...' : `Add ${activeCollectionKind === 'project' ? 'project' : 'skills'}`}
            </button>
          </div>
        ) : null}

        {canHaveChildren && capabilities.canAddChild && (!simpleMode || isSimpleSectionNode(safeItem)) ? (
          <>
            <h6 className="mt-3">Content inside</h6>
            <p className="text-muted small mb-2">Select content on the page to edit it directly.</p>
            <button className="btn btn-outline-dark btn-sm w-100" type="button" onClick={() => openAddMenu('child')}>
              <i className="bi bi-file-earmark-plus me-2" aria-hidden="true"></i>{simpleMode ? 'Add content inside' : 'Add inside'}
            </button>
          </>
        ) : null}

        </SideMenu>
      ) : null}

      {renderAddMenu ? (
        <AddItemsMenu
          showAddMenu={showAddMenu}
          setShowAddMenu={(value) => {
            setShowAddMenu(value);
            if (!value) setDrag?.(true);
          }}
          onAddItem={addForInsertionMode}
          userId={userId}
          disallowedItems={disallowedItems}
          requestConfirmation={requestConfirmation}
          editorMode={editorMode}
          aiEnabled={aiEnabled}
          onOpenAISection={() => onOpenAISection?.(safeItem.id, insertionMode)}
        />
      ) : null}

      {!simpleMode && renderClassSelector ? (
        <ClassSelectorMenu
          expanded={showClassSelector}
          setExpanded={(value) => value ? openClassSelector() : closeClassSelector()}
          appliedClasses={nodeClasses}
          customClasses={customClasses}
          onToggleClass={toggleClass}
          onCreateCustomClass={createCustomClass}
          onOpenClassStyles={openClassStylesFromSelector}
        />
      ) : null}

      {showGallery ? (
        <Suspense fallback={focusedControlFallback}>
          <ImageGallery
            userId={userId}
            setShowGallery={setShowGallery}
            addElement={changeSelectedImage}
            requestConfirmation={requestConfirmation}
          />
        </Suspense>
      ) : null}

      {showProjectsGallery ? (
        <Suspense fallback={focusedControlFallback}>
          <ProjectsGallery
            userId={userId}
            onSelect={(projectIds) => void applyCollectionIds(projectIds)}
            onClose={() => setShowProjectsGallery(false)}
            multiSelect
            initialSelectedIds={activeCollectionRecordIds}
          />
        </Suspense>
      ) : null}

      {showSkillsGallery ? (
        <Suspense fallback={focusedControlFallback}>
          <div className="new-blog-skills-selection">
            <SkillsGallery
              userId={userId}
              onSelect={(skillIds) => void applyCollectionIds(skillIds)}
              setExpanded={setShowSkillsGallery}
              multiSelect
              initialSelectedIds={activeCollectionRecordIds}
            />
          </div>
        </Suspense>
      ) : null}
    </div>
  );
};

const DraggableEditItem = (props) => {
  const capabilities = getManagedNodeCapabilities(props.item);
  const nestedDrag = useNestedElementDrag({
    nodeId: props.item?.id || '',
    depth: props.depth || 0,
    enabled: props.dragEnabled,
    canMove: capabilities.canMove,
    canAddChild: capabilities.canAddChild,
    canContainElements: canPortfolioElementContainElements(props.item?.type),
    onMoveNode: props.onMoveNode,
  });

  return <EditItemContent {...props} nestedDrag={nestedDrag} />;
};

const StaticEditItem = (props) => <EditItemContent {...props} nestedDrag={NO_DRAG} />;

const subtreeContainsNode = (node, nodeId) => {
  if (!nodeId || !node) return false;
  if (node.id === nodeId) return true;
  return Array.isArray(node.children)
    && node.children.some((child) => subtreeContainsNode(child, nodeId));
};

const selectionTouchesSubtree = (previous, next) => (
  [
    previous.focusedNodeId,
    next.focusedNodeId,
    previous.editPanelNodeId,
    next.editPanelNodeId,
  ].some((nodeId) => subtreeContainsNode(next.item, nodeId))
);

export const editorItemPropsAreEqual = (previous, next) => {
  if (previous.item !== next.item
    || previous.index !== next.index
    || previous.siblingCount !== next.siblingCount
    || previous.dragSupported !== next.dragSupported
    || previous.dragEnabled !== next.dragEnabled
    || previous.compactActions !== next.compactActions
    || previous.editorMode !== next.editorMode
    || previous.depth !== next.depth
    || previous.insideBootstrapRow !== next.insideBootstrapRow
    || previous.resolvedCustomClasses !== next.resolvedCustomClasses) {
    return false;
  }

  const previousPath = previous.activeEditorPathIds;
  const nextPath = next.activeEditorPathIds;
  if (previousPath instanceof Set && nextPath instanceof Set) {
    if (previousPath === nextPath) return true;
    const nodeId = next.item?.id;
    return !previousPath.has(nodeId) && !nextPath.has(nodeId);
  }

  // Keep direct component consumers compatible when no precomputed path is supplied.
  return !selectionTouchesSubtree(previous, next);
};

const EditItemWithControls = (props) => {
  // Keep the desktop shell stable while menus temporarily disable its handlers.
  const controlStateBundle = useFocusedControlState(props.item?.type || 'div');
  const dragSupported = props.dragSupported ?? props.dragEnabled;
  return dragSupported
    ? <DraggableEditItem {...props} controlStateBundle={controlStateBundle} />
    : <StaticEditItem {...props} controlStateBundle={controlStateBundle} />;
};

// Keeping the drag hook outside the static path means phones do not allocate drag state per node.
const EditItem = memo(EditItemWithControls, editorItemPropsAreEqual);

EditItem.propTypes = {
  index: PropTypes.number.isRequired,
  siblingCount: PropTypes.number,
  item: PropTypes.object.isRequired,
  editJSONItem: PropTypes.func.isRequired,
  addItem: PropTypes.func.isRequired,
  swapItems: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  focusedNodeId: PropTypes.string,
  editPanelNodeId: PropTypes.string,
  activeEditorPathIds: PropTypes.instanceOf(Set),
  compactActions: PropTypes.bool,
  onOpenPhoneRearrange: PropTypes.func,
  setEditPanelNodeId: PropTypes.func,
  setDrag: PropTypes.func,
  dragSupported: PropTypes.bool,
  dragEnabled: PropTypes.bool,
  onRootDragStart: PropTypes.func,
  onRootDragEnd: PropTypes.func,
  onMoveNode: PropTypes.func,
  onUpdateNode: PropTypes.func,
  onInsertNode: PropTypes.func,
  onRemoveNode: PropTypes.func,
  onReorderNode: PropTypes.func,
  layerMoveOptions: PropTypes.shape({
    canChangeLayer: PropTypes.bool,
    canMoveUp: PropTypes.bool,
    nodeLabel: PropTypes.string,
    parentCandidates: PropTypes.array,
  }),
  onMoveUpLayer: PropTypes.func,
  onOpenLayerMoveMenu: PropTypes.func,
  userId: PropTypes.string,
  editorDocument: PropTypes.object,
  setEditorDocument: PropTypes.func,
  disallowedItems: PropTypes.arrayOf(PropTypes.string),
  onOpenClassStyleMenu: PropTypes.func,
  aiEnabled: PropTypes.bool,
  requestConfirmation: PropTypes.func,
  resolvedCustomClasses: PropTypes.object,
  depth: PropTypes.number,
  insideBootstrapRow: PropTypes.bool,
  collectionController: PropTypes.shape({
    kind: PropTypes.oneOf(['project', 'skill']).isRequired,
    recordIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    onUpdateRecords: PropTypes.func.isRequired,
  }),
  editorMode: PropTypes.oneOf(Object.values(EDITOR_MODES)),
  onOpenAISection: PropTypes.func,
};

export default EditItem;
